import { TabPanelInner } from 'components/Tabs';
import { useAppSelector } from 'store/hooks';
import {
  selectConnectionQualityLevel,
  selectConnectionQualityV2Enabled,
  selectIndicatorAverageMOS,
  selectNetworkTotals,
} from 'features/system-stats/systemStatsSlice';
import { ConnectionQualityLevel } from 'utils/webrtc/ConnectionQuality';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import StatsItem from 'features/room/settings/StatsItem';
import { useTranslation } from 'react-i18next';
import { toFixedNumber } from 'utils/math';
import AggregatorPanel from 'features/system-stats/AggregatorPanel';
import PerTrackScoresTable from 'features/system-stats/PerTrackScoresTable';
import { bucketColor, MosBucket } from 'features/system-stats/mosBucket';
import { DirectionalTotals } from 'utils/webrtc/stats/types';

const formatMs = (ms: number | null): string => (ms === null ? '—' : `${Math.round(ms)}ms`);

const RTT_TOOLTIP =
  'RTT is measured per-track when the remote sends RTCP timing reports; otherwise it falls back to the ICE transport.';

const QUALITY_KEY_BY_BUCKET: Record<MosBucket, string> = {
  unknown: 'system_stats.quality_levels.bad',
  bad: 'system_stats.quality_levels.bad',
  poor: 'system_stats.quality_levels.poor',
  fair: 'system_stats.quality_levels.fair',
  good: 'system_stats.quality_levels.good',
};

// Mirrors ConnectionIndicator's level→color mapping, so this debug readout
// matches the real quality dot instead of re-deriving its own bucket from MOS.
const BUCKET_BY_QUALITY_LEVEL: Record<ConnectionQualityLevel, MosBucket> = {
  [ConnectionQualityLevel.poor]: 'poor',
  [ConnectionQualityLevel.fair]: 'fair',
  [ConnectionQualityLevel.good]: 'good',
};

const SystemStatsTab = () => {
  const { t } = useTranslation('settings');
  const theme = useTheme();
  const networkTotals = useAppSelector(selectNetworkTotals);
  // The v2 headline reads the same smoothed signal the indicator dot reads, so
  // the number + colour here match the dot (not the raw min-of-all aggregate).
  const indicatorMOS = useAppSelector(selectIndicatorAverageMOS);
  const connectionQualityLevel = useAppSelector(selectConnectionQualityLevel);
  const v2Enabled = useAppSelector(selectConnectionQualityV2Enabled);

  // Reads the same hysteresis-derived level as the real quality dot, instead
  // of re-bucketing the raw MOS, so this label doesn't flap independently.
  const printQualityLevel = (level: ConnectionQualityLevel | null) => {
    const bucket = level === null ? 'unknown' : BUCKET_BY_QUALITY_LEVEL[level];
    return (
      <Box component="span" sx={{ color: bucketColor(theme, bucket) }}>
        {t(QUALITY_KEY_BY_BUCKET[bucket])}
      </Box>
    );
  };

  const renderDirectionColumn = (label: string, totals: DirectionalTotals) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography component="h3" variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
      <StatsItem label="Packets" value={totals.packets} />
      <StatsItem label="Packets lost" value={totals.packetsLost} />
      <StatsItem label="Packet loss" value={`${totals.packetLossPct}%`} />
      <StatsItem label="Jitter" value={formatMs(totals.jitterMs)} />
      <StatsItem label="Round trip time" value={formatMs(totals.rttMs)} tooltip={RTT_TOOLTIP} />
    </Box>
  );

  const directionCount =
    (networkTotals.sending !== null ? 1 : 0) + (networkTotals.receiving !== null ? 1 : 0);
  const hasNetworkTotals = directionCount > 0;

  return (
    <TabPanelInner sx={{ maxWidth: 'none' }}>
      {v2Enabled && hasNetworkTotals && (
        <Box sx={{ mb: 4 }}>
          <Typography component="h2" variant="h6" gutterBottom>
            Network totals
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${directionCount}, minmax(0, 1fr))`,
              gap: 4,
              alignItems: 'flex-start',
            }}
          >
            {networkTotals.sending !== null &&
              renderDirectionColumn('Sending', networkTotals.sending)}
            {networkTotals.receiving !== null &&
              renderDirectionColumn('Receiving', networkTotals.receiving)}
          </Box>
        </Box>
      )}

      {v2Enabled && (
        <>
          <Typography component="h2" variant="h6" gutterBottom>
            {t('system_stats.stats_section_title')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography component="span" sx={{ fontWeight: 700, mr: 2 }}>
              {t('system_stats.connection_quality')}
            </Typography>
            <Typography component="span">
              {printQualityLevel(connectionQualityLevel)} (MOS{' '}
              {indicatorMOS !== null ? toFixedNumber(indicatorMOS, 2) : '—'})
            </Typography>
          </Box>
          <AggregatorPanel />
          <PerTrackScoresTable />
        </>
      )}
    </TabPanelInner>
  );
};
export default SystemStatsTab;
